import { Chip, ChipVariants } from "components/Chip";
import { validationConstants } from "constants/formValidation";
import { orderStatusConstants, orderTypeConstants } from "constants/order";
import {
  distributionLogicOptions,
  distributionLogicValueList,
} from "constants/paymentPlanConstants";
import { ToastContext } from "context/ToastContext";
import { parseDate } from "helpers/date";
import { parsePrice, centsToDollars, dollarsToCents } from "helpers/price";
import useFormValidation from "hooks/useFormValidation";
import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import { handleChangeDateWithMask } from "pages/CreateOrder/utils/mask";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { ToggleButton } from "primereact/togglebutton";
import { useEffect, useState, useContext, useMemo, useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useUpdateOrderMutation } from "store/queries/order";
import styled from "styled-components";
import { IAccount } from "types/Account/Account";
import { IOrder } from "types/Order/Order";
import { OrderType } from "types/Order/OrderType";
import { IPayment, IPaymentPlan, IInstallment } from "types/Payment";

import { RemainingAmountEditTable } from "./components/RemainingAmountEditTable";
import {
  validateNextInstallmentDate,
  validateMinDownPaymentFlat,
} from "./helpers/helper";

export interface IPaymentDetailsEdit {
  paymentPlanMinDownPaymentPercent: number | null;
  paymentPlanDuration: number | null;
  paymentPlanRemainingAmount: number | null;
  paymentPlanMinDownPaymentType?: string;
  paymentPlanMinDownPaymentFlat?: number | null;
  installments?: IInstallment[];
  paymentPlanEnabled: boolean;
  nextInstallmentDate: string;
  providerDistributionLogic?: distributionLogicValueList | null;
  differenceByProvider?: any;
  valuesByProvider?: any;
  nextInstallmentDateChanged?: boolean;
}

export interface IProviderRemainingAmountRow {
  providerId: string;
  providerName: string;
  cptCode: string;
  providerPriority: number;
  totalExpectedTransferAmount: number;
}

const StyledCard = styled(Card)`
  margin-bottom: ${sizer(2)};
  flex-grow: 1;
  border: 1px solid var(--gray-new-200) !important;
`;

function PaymentDetails({
  payments,
  paymentPlan,
  orderId,
  onUpdateOrder,
  editable,
  showPaymentPlan,
  orderStatus,
  account,
  showPaymentPlanEnableButton,
  paymentPlanBreakDown,
  orderType,
  order,
  paymentPlanRemaining,
}: {
  payments?: IPayment[];
  paymentPlan: IPaymentPlan;
  orderId: string;
  onUpdateOrder: () => void;
  editable: boolean;
  showPaymentPlan: boolean;
  showPaymentPlanEnableButton: boolean;
  orderStatus: string;
  account: IAccount;
  paymentPlanBreakDown?: any;
  orderType: OrderType;
  order?: IOrder;
  paymentPlanRemaining?: Array<{
    providerId: string;
    remainingAmountInCents: number;
    providerPriority?: number | null;
  }>;
}) {
  const [editing, setEditing] = useState(false);
  const [submitButtonEnabled, setSubmitButtonEnabled] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [saveButtonLoading, setSaveButtonLoading] = useState(false);
  const [
    initialRemainingInstallmentsCount,
    setInitialRemainingInstallmentsCount,
  ] = useState<number | null>(null);
  const {
    control,
    getValues,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<IPaymentDetailsEdit>();
  const [updateOrder] = useUpdateOrderMutation();
  const { current: toastElement } = useContext(ToastContext);
  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();

  // Get total patient portion from order data
  const totalPatientPortion = useMemo(() => {
    if (orderType !== orderTypeConstants.GFE) {
      return order?.amountInCents;
    }
    const serviceDetails = order?.serviceDetails;
    if (!serviceDetails) return null;

    // We use updated amount if available
    // We cast to any to access the dynamic properties since TypeScript doesn't know about these fields
    const details = serviceDetails as any;
    return (
      details.updatedPatientResponsibilityEstimateAmountInCents ||
      details.patientResponsibilityEstimateAmountInCents ||
      null
    );
  }, [order?.serviceDetails, orderType, order?.amountInCents]);

  // Create validation function that uses the helper with current state
  const validateMinDownPaymentFlatWithState = useCallback(
    (value: number | null | undefined) => {
      return validateMinDownPaymentFlat(
        value,
        totalPatientPortion,
        order?.clearPatientFeeValueInPercentage,
        order?.status
      );
    },
    [totalPatientPortion, order?.clearPatientFeeValueInPercentage]
  );

  const resetForm = () => {
    setValue(
      "paymentPlanMinDownPaymentPercent",
      paymentPlan?.paymentPlanMinDownPaymentPercent
    );
    setValue(
      "paymentPlanRemainingAmount",
      paymentPlan?.remainingAmountInCents
        ? centsToDollars(paymentPlan?.remainingAmountInCents)
        : null
    );
    setValue(
      "paymentPlanMinDownPaymentFlat",
      paymentPlan?.paymentPlanMinDownPaymentFlat
        ? centsToDollars(paymentPlan?.paymentPlanMinDownPaymentFlat)
        : null
    );
    setValue(
      "paymentPlanMinDownPaymentType",
      paymentPlan?.paymentPlanMinDownPaymentFlat ? "Flat amount" : "Percentage"
    );
    setValue("paymentPlanEnabled", !!paymentPlan);
    setValue(
      "providerDistributionLogic",
      paymentPlan?.providerDistributionLogic ||
        account?.providerDistributionLogic ||
        distributionLogicValueList.BY_PRIORITY
    );
    setValue("nextInstallmentDateChanged", false);

    // Set Value for paymentPlanDuration and nextInstallmentDate
    if (!paymentPlan?.installments?.length) {
      setValue("nextInstallmentDate", "");
      const duration = paymentPlan?.paymentPlanDuration || 0;
      setValue("paymentPlanDuration", duration);
      setInitialRemainingInstallmentsCount(duration);
      return;
    }
    // Sort installments Array by dateDue to make sure we get the first installment date
    // Include all installments regardless of date to account for failed installments
    const sortedInstallments = [...paymentPlan.installments].sort(
      (a, b) => new Date(a.dateDue).getTime() - new Date(b.dateDue).getTime()
    ); // Sort by dateDue

    // remaining installments with null paymentId
    const nullPaymentIdCount = sortedInstallments.reduce(
      (count, installment) => {
        return !installment.paymentId ? count + 1 : count;
      },
      0
    );
    // first unpaid installment (by due date)
    const firstNullPaymentIdInstallment = sortedInstallments.find(
      (installment) => !installment.paymentId
    );
    const nextInstallmentDate = firstNullPaymentIdInstallment?.dateDue
      ? parseDate(firstNullPaymentIdInstallment.dateDue, false, true)
      : "-";

    setValue("paymentPlanDuration", nullPaymentIdCount);
    setValue("nextInstallmentDate", nextInstallmentDate);
    setInitialRemainingInstallmentsCount(nullPaymentIdCount);
  };

  useEffect(() => {
    if (!isFirstRender) {
      return;
    }
    resetForm();
    setIsFirstRender(false);
  }, [paymentPlan, setValue, account]);

  const minDownPaymentType = watch("paymentPlanMinDownPaymentType");
  const minDownPaymenFlat = watch("paymentPlanMinDownPaymentFlat");
  const minDownPaymentPercent = watch("paymentPlanMinDownPaymentPercent");
  const nextInstallmentDateField = watch("nextInstallmentDate");
  const remainingAmountField = watch("paymentPlanRemainingAmount");
  const remainingInstallmentsField = watch("paymentPlanDuration");

  // Calculate display value for minimum down payment flat without clear patient fee, when it exists
  const minDownPaymentFlatDisplayValue = useMemo(() => {
    return minDownPaymenFlat &&
      order?.clearPatientFeeValueInPercentage &&
      orderType === orderTypeConstants.GFE
      ? minDownPaymenFlat / (1 + order.clearPatientFeeValueInPercentage / 100)
      : minDownPaymenFlat;
  }, [minDownPaymenFlat, order?.clearPatientFeeValueInPercentage, orderType]);

  // Helper function to calculate payment breakdown data
  const calculatePaymentBreakdown = useCallback(
    (
      paymentPlanBreakDown: any,
      hasClearPatientFee: boolean,
      paymentPlanRemaining?: any[]
    ) => {
      if (!paymentPlanBreakDown?.payments?.length) {
        return { providers: [], accurateTotal: null };
      }

      const paymentsWithoutInitialDeposit =
        paymentPlanBreakDown.payments.filter((p: any) => !!p.installmentInDb);
      const paymentsStillNotPaid = paymentsWithoutInitialDeposit.filter(
        (p: any) => !p.installmentInDb.paymentId
      );

      const providerMap = new Map();
      let totalWithClearPatientFee = 0;

      // Build the provider map from breakdown data to get metadata
      // Use ALL payments (including initial deposit) to ensure we have provider metadata
      paymentPlanBreakDown.payments.forEach((payment: any) => {
        payment.providerBreakdown.forEach((provider: any) => {
          const { providerId, providerName, providerPriority, cptCode } =
            provider;
          if (!providerMap.has(providerId)) {
            providerMap.set(providerId, {
              providerId,
              providerName,
              providerPriority,
              cptCode,
              totalExpectedTransferAmount: 0,
            });
          }
        });
      });

      // Calculate accurate total from unpaid payments only (for clear patient fee calculation)
      paymentsStillNotPaid.forEach((payment: any) => {
        payment.providerBreakdown.forEach((provider: any) => {
          // For accurate total calculation (only when clear patient fee exists)
          if (hasClearPatientFee) {
            totalWithClearPatientFee +=
              provider.expectedTransferAmount +
              provider.clearFeeAmountInCents +
              (provider.clearPatientFeeAmountInCents || 0);
          }
        });
      });

      // Set remaining amounts from backend data
      if (paymentPlanRemaining?.length) {
        paymentPlanRemaining.forEach((remaining) => {
          if (providerMap.has(remaining.providerId)) {
            providerMap.get(remaining.providerId).totalExpectedTransferAmount =
              remaining.remainingAmountInCents;
          }
        });
      }

      return {
        providers: Array.from(providerMap.values()),
        accurateTotal: hasClearPatientFee ? totalWithClearPatientFee : null,
      };
    },
    []
  );

  // Calculate both provider breakdown and accurate total in one helper function
  const paymentBreakdownResult = useMemo(() => {
    const hasClearPatientFee =
      order?.clearPatientFeeValueInPercentage &&
      order.clearPatientFeeValueInPercentage > 0;
    return calculatePaymentBreakdown(
      paymentPlanBreakDown,
      !!hasClearPatientFee,
      paymentPlanRemaining
    );
  }, [
    paymentPlanBreakDown,
    order?.clearPatientFeeValueInPercentage,
    calculatePaymentBreakdown,
    paymentPlanRemaining,
  ]);

  const remainingAmountBreakdown = paymentBreakdownResult.providers;
  const accurateRemainingTotal = paymentBreakdownResult.accurateTotal;

  const [differenceByProvider, setDifferenceByProvider] = useState<{
    [key: string]: number;
  }>({});

  const [valuesByProvider, setValuesByProvider] = useState<{
    [key: string]: number;
  }>({});

  const minDownPaymentEnabled = useMemo(() => {
    if (orderStatus !== orderStatusConstants.SENT_TO_PATIENT || !editing) {
      return false;
    }
    return true;
  }, [orderStatus, editing]);

  const showPercentageField = useMemo(() => {
    if (!minDownPaymentType) {
      return false;
    }
    if (minDownPaymentType === "Percentage") {
      return true;
    }
    return false;
  }, [minDownPaymentType]);

  const showFlatAmountField = useMemo(() => {
    if (!minDownPaymentType) {
      return false;
    }
    if (minDownPaymentType === "Flat amount") {
      return true;
    }
    return false;
  }, [minDownPaymentType]);

  const showNextInstallmentDate = useMemo(
    () =>
      orderStatus === orderStatusConstants.PARTIALLY_PAID ||
      (editing &&
        orderStatus === orderStatusConstants.PAID &&
        !!paymentPlanBreakDown?.payments?.length),
    [orderStatus, paymentPlan, editing, paymentPlanBreakDown]
  );

  useEffect(() => {
    if (isFirstRender) {
      return;
    }
    if (minDownPaymentType === "Percentage") {
      setValue("paymentPlanMinDownPaymentFlat", null);
      setValue(
        "paymentPlanMinDownPaymentPercent",
        paymentPlan?.paymentPlanMinDownPaymentPercent || null
      );
    } else if (minDownPaymentType === "Flat amount") {
      setValue("paymentPlanMinDownPaymentPercent", null);
      setValue(
        "paymentPlanMinDownPaymentFlat",
        paymentPlan?.paymentPlanMinDownPaymentFlat
          ? centsToDollars(paymentPlan.paymentPlanMinDownPaymentFlat)
          : null
      );
    }
  }, [minDownPaymentType]);

  useEffect(() => {
    const validateFields = async () => {
      let isValidFlatAmount = false;
      let isValidNextInstallmentDate = false;
      let isValidRemainingAmount = false;
      const isValidRemainingInstallments =
        remainingInstallmentsField !== null &&
        remainingInstallmentsField !== undefined;
      const isPercentageType = minDownPaymentType === "Percentage";
      const isFlatValid = await trigger("paymentPlanMinDownPaymentFlat");
      const isPercentageValid = await trigger(
        "paymentPlanMinDownPaymentPercent"
      );
      if (isPercentageType) {
        isValidFlatAmount = !!isPercentageValid;
      } else {
        isValidFlatAmount = !!isFlatValid;
      }
      if (!showNextInstallmentDate) {
        isValidNextInstallmentDate = true;
      } else {
        const validNextInstallmentDate = validateNextInstallmentDate(
          nextInstallmentDateField,
          "future"
        );
        isValidNextInstallmentDate =
          typeof validNextInstallmentDate !== "string";
      }
      if (orderStatus === orderStatusConstants.SENT_TO_PATIENT) {
        isValidRemainingAmount = true;
      } else {
        isValidRemainingAmount =
          remainingAmountField !== null && remainingAmountField !== undefined;
      }
      setSubmitButtonEnabled(
        isValidFlatAmount &&
          isValidNextInstallmentDate &&
          isValidRemainingAmount &&
          isValidRemainingInstallments
      );
    };
    validateFields();
  }, [
    minDownPaymenFlat,
    minDownPaymentPercent,
    trigger,
    minDownPaymentType,
    nextInstallmentDateField,
    remainingInstallmentsField,
    remainingAmountField,
    totalPatientPortion,
  ]);

  const paidPayment: IPayment | undefined = useMemo(() => {
    const firstPaidPayment = payments?.find(
      (aPayment) => aPayment.status === "Paid"
    );

    if (!firstPaidPayment || !firstPaidPayment.splitTotalPaymentId) {
      return firstPaidPayment;
    }

    // If this is a split payment, find all payments with the same splitTotalPaymentId
    const splitPayments = payments?.filter(
      (payment) =>
        payment.splitTotalPaymentId === firstPaidPayment.splitTotalPaymentId &&
        payment.status === "Paid"
    );

    // Calculate total amount across all split payments
    const totalAmountInCents = splitPayments?.reduce(
      (total, payment) => total + (payment.amountInCents || 0),
      0
    );

    // Return the first payment but with the total amount from all split payments
    return {
      ...firstPaidPayment,
      amountInCents: totalAmountInCents,
    };
  }, [payments]);

  const refundedPayment: IPayment | undefined = payments?.find(
    (aPayment) => aPayment.status === "Refunded"
  );

  const mapPaymentDetailsUpdateRequest = (values: IPaymentDetailsEdit) => {
    return {
      paymentPlan: [
        {
          remainingAmountInCents:
            values.paymentPlanRemainingAmount !== null &&
            values.paymentPlanRemainingAmount !== undefined
              ? dollarsToCents(values.paymentPlanRemainingAmount)
              : null,
          paymentPlanDuration: values.paymentPlanDuration,
          paymentPlanMinDownPaymentPercent:
            values.paymentPlanMinDownPaymentPercent,
          active: values.paymentPlanEnabled,
          hasInstallments: !!paymentPlanBreakDown?.payments?.length,
          paymentPlanMinDownPaymentFlat: values.paymentPlanMinDownPaymentFlat
            ? dollarsToCents(values.paymentPlanMinDownPaymentFlat)
            : null,
          nextInstallmentDate: values.nextInstallmentDate || null,
          providerDistributionLogic:
            orderType === orderTypeConstants.PATIENT_RESPONSIBILITY
              ? null
              : values.providerDistributionLogic,
          differenceByProvider: values?.differenceByProvider,
          valuesByProvider: values?.valuesByProvider,
          nextInstallmentDateChanged: values?.nextInstallmentDateChanged,
        },
      ],
    };
  };

  const submit = () => {
    setSaveButtonLoading(true);
    setValue("differenceByProvider", differenceByProvider);
    setValue("valuesByProvider", valuesByProvider);
    const values = mapPaymentDetailsUpdateRequest(getValues());
    updateOrder({ id: orderId, ...values })
      .unwrap()
      .then(() => {
        setSaveButtonLoading(false);
        onUpdateOrder();
        setEditing(false);
        setDifferenceByProvider({});
        setValuesByProvider({});
        setValue("differenceByProvider", null);
        setValue("valuesByProvider", null);
        toastElement?.show({
          summary: "Success!",
          severity: "success",
          detail: "Your order has been updated.",
        });
      })
      .catch(() => {
        toastElement?.show({
          severity: "error",
          detail: "Try again later.",
          summary: "Something went wrong.",
        });
      });
  };

  const remainingAmountDisabled = useMemo(() => {
    if (orderStatus === orderStatusConstants.SENT_TO_PATIENT) {
      return true;
    }
    return !editing;
  }, [orderStatus, editing]);

  const depositDate = useMemo(() => {
    return paidPayment?.stripePayout?.payoutArrivalDate
      ? parseDate(paidPayment.stripePayout.payoutArrivalDate, false, true)
      : "-";
  }, [paidPayment]);

  const showRemainingAmountsSection = useMemo(() => {
    return (
      editing &&
      (orderStatus === orderStatusConstants.PARTIALLY_PAID ||
        orderStatus === orderStatusConstants.PAID) &&
      orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY
    );
  }, [editing, orderStatus, orderType]);

  const showRemainingAmountField = useMemo(() => {
    if (orderType === orderTypeConstants.PATIENT_RESPONSIBILITY) {
      return true;
    }
    return !editing;
  }, [editing, orderStatus]);

  const editButtonDisabled = useMemo(() => {
    if (
      (orderStatus === orderStatusConstants.PARTIALLY_PAID ||
        orderStatus === orderStatusConstants.PAID) &&
      paymentPlanBreakDown?.payments?.length
    ) {
      const iniitalDeposit = paymentPlanBreakDown.payments[0];
      const paymentsWithoutInitialDeposit =
        paymentPlanBreakDown.payments.filter((p: any) => !!p.installmentInDb);
      const lastPaidInstallment = paymentsWithoutInitialDeposit.reduce(
        (last: any, p: any) => (p.installmentInDb?.paymentId ? p : last),
        null
      );
      if (lastPaidInstallment) {
        return !lastPaidInstallment.transferredToProviders;
      }
      return !iniitalDeposit.transferredToProviders;
    }
    return false;
  }, [paymentPlanBreakDown, orderStatus]);

  const isPaymentPlanRemainingAmountChanged = useMemo(() => {
    return (
      dollarsToCents(remainingAmountField || 0) !==
      paymentPlan?.remainingAmountInCents
    );
  }, [remainingAmountField, paymentPlan?.remainingAmountInCents]);

  // Calculation function for monthly payment and recommendations

  const calculatePaymentInfo = (
    currentAmount: number,
    initialAmount: number,
    currentInstallments: number,
    hasChanges: boolean
  ) => {
    if (!currentInstallments || currentInstallments <= 0) {
      return {
        monthlyPaymentInfo: null,
        recommendationInfo: null,
        adjustedInstallments: null,
      };
    }

    const currentMonthlyPayment = currentAmount / currentInstallments;

    const currentMonthlyText = parsePrice(
      centsToDollars(currentMonthlyPayment)
    );

    // Only show "previously" if we have valid initial data
    const monthlyPaymentInfo =
      hasChanges &&
      initialRemainingInstallmentsCount &&
      initialRemainingInstallmentsCount > 0
        ? (() => {
            const previousMonthlyPayment =
              initialAmount / initialRemainingInstallmentsCount;
            const previousMonthlyText = parsePrice(
              centsToDollars(previousMonthlyPayment)
            );
            return `${currentMonthlyText} (previously ${previousMonthlyText})`;
          })()
        : `${currentMonthlyText}`;

    // Calculate recommendation
    let recommendationInfo = null;
    if (
      hasChanges &&
      initialRemainingInstallmentsCount &&
      initialRemainingInstallmentsCount > 0
    ) {
      // Use the ORIGINAL installments count for the initial monthly payment calculation
      const initialPreviousMonthlyPayment =
        initialAmount / initialRemainingInstallmentsCount;

      // Calculate the best possible duration to get close to initial monthly payment
      const idealMonths = currentAmount / initialPreviousMonthlyPayment;
      let bestPossibleDurationCount = Math.max(1, Math.round(idealMonths));
      let recommendedMonthlyPayment = currentAmount / bestPossibleDurationCount;

      // Check if recommended monthly payment exceeds 6% above original
      const maxAllowedMonthlyPayment = initialPreviousMonthlyPayment * 1.06; // 6% above original
      if (recommendedMonthlyPayment > maxAllowedMonthlyPayment) {
        // Use the 2nd closest option (more months, lower payment)
        bestPossibleDurationCount += 1;
        recommendedMonthlyPayment = currentAmount / bestPossibleDurationCount;
      }

      // Check if the difference is significant (more than 7 cents)
      const monthlyPaymentDifference = Math.abs(
        currentMonthlyPayment - initialPreviousMonthlyPayment
      );
      const recommendedDifference = Math.abs(
        recommendedMonthlyPayment - initialPreviousMonthlyPayment
      );

      // Only show recommendation if:
      // 1. The current difference is more than $0.07 (7 cents)
      // 2. The recommended option is better than current
      // 3. The recommended duration is different from current
      if (
        monthlyPaymentDifference > 7 &&
        recommendedDifference < monthlyPaymentDifference &&
        bestPossibleDurationCount !== currentInstallments
      ) {
        const initialPreviousMonthlyText = parsePrice(
          centsToDollars(initialPreviousMonthlyPayment)
        );

        recommendationInfo = `The number of months has been automatically adjusted to ${bestPossibleDurationCount} to keep the monthly payment close to ${initialPreviousMonthlyText}.`;
        return {
          monthlyPaymentInfo,
          recommendationInfo,
          adjustedInstallments: bestPossibleDurationCount,
        };
      }
    }

    return {
      monthlyPaymentInfo,
      recommendationInfo,
      adjustedInstallments: null,
    };
  };

  // Monthly payment calculation for Patient Responsibility orders
  const monthlyPaymentInfo = useMemo(() => {
    if (
      orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY ||
      !editing ||
      !remainingInstallmentsField ||
      remainingInstallmentsField <= 0 ||
      orderStatus !== orderStatusConstants.PARTIALLY_PAID
    ) {
      return null;
    }

    const currentRemainingAmount = remainingAmountField
      ? dollarsToCents(remainingAmountField)
      : 0;
    const initialRemainingAmount = paymentPlan?.remainingAmountInCents || 0;
    const hasChanges = currentRemainingAmount !== initialRemainingAmount;

    const { monthlyPaymentInfo } = calculatePaymentInfo(
      currentRemainingAmount,
      initialRemainingAmount,
      remainingInstallmentsField,
      hasChanges
    );

    return monthlyPaymentInfo;
  }, [
    orderType,
    editing,
    remainingInstallmentsField,
    remainingAmountField,
    paymentPlan?.remainingAmountInCents,
    initialRemainingInstallmentsCount,
  ]);

  // State to persist the attention message
  const [persistentRecommendation, setPersistentRecommendation] = useState<
    string | null
  >(null);

  // Track the last remaining amount that triggered auto-adjustment
  const [lastAdjustedAmount, setLastAdjustedAmount] = useState<number | null>(
    null
  );

  // Track previous installments to detect manual changes
  const [prevInstallments, setPrevInstallments] = useState<number | null>(
    remainingInstallmentsField
  );

  // Recommendation calculation for Patient Responsibility orders
  const recommendationData = useMemo(() => {
    if (
      orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY ||
      !editing ||
      !remainingInstallmentsField ||
      remainingInstallmentsField <= 0 ||
      !initialRemainingInstallmentsCount ||
      initialRemainingInstallmentsCount <= 0 ||
      orderStatus !== orderStatusConstants.PARTIALLY_PAID
    ) {
      return { recommendationInfo: null, adjustedInstallments: null };
    }

    const currentRemainingAmount = remainingAmountField
      ? dollarsToCents(remainingAmountField)
      : 0;
    const initialRemainingAmount = paymentPlan?.remainingAmountInCents || 0;
    const hasChanges = currentRemainingAmount !== initialRemainingAmount;

    if (!hasChanges) {
      return { recommendationInfo: null, adjustedInstallments: null };
    }

    const { recommendationInfo, adjustedInstallments } = calculatePaymentInfo(
      currentRemainingAmount,
      initialRemainingAmount,
      initialRemainingInstallmentsCount,
      hasChanges
    );

    return { recommendationInfo, adjustedInstallments };
  }, [
    orderType,
    editing,
    remainingInstallmentsField,
    remainingAmountField,
    paymentPlan?.remainingAmountInCents,
    initialRemainingInstallmentsCount,
    orderStatus,
  ]);

  const { recommendationInfo, adjustedInstallments } = recommendationData;

  // Auto-update the Remaining Installments field when remaining amount changes
  useEffect(() => {
    const currentAmount = remainingAmountField
      ? dollarsToCents(remainingAmountField)
      : 0;
    const amountChanged = currentAmount !== lastAdjustedAmount;

    if (
      recommendationInfo &&
      adjustedInstallments &&
      editing &&
      amountChanged
    ) {
      // Save the recommendation message to persist it
      setPersistentRecommendation(recommendationInfo);

      // Use the adjusted installments number directly
      if (adjustedInstallments !== remainingInstallmentsField) {
        // Update tracking BEFORE setValue to prevent the "hide" useEffect from triggering
        setLastAdjustedAmount(currentAmount);
        setPrevInstallments(adjustedInstallments);
        setValue("paymentPlanDuration", adjustedInstallments);
      }
    }
  }, [
    recommendationInfo,
    adjustedInstallments,
    editing,
    remainingAmountField,
    setValue,
    lastAdjustedAmount,
  ]);

  // Hide message when user manually changes installments
  useEffect(() => {
    if (
      prevInstallments !== null &&
      remainingInstallmentsField !== prevInstallments
    ) {
      // Check if this is from our auto-adjustment by comparing with adjustedInstallments
      // If the field matches adjustedInstallments, it's our auto-update, not a manual change
      if (
        adjustedInstallments &&
        remainingInstallmentsField === adjustedInstallments
      ) {
        // This is our auto-adjustment, just update prevInstallments
        setPrevInstallments(remainingInstallmentsField);
      } else {
        // User manually changed installments - hide the message
        setPersistentRecommendation(null);
        setPrevInstallments(remainingInstallmentsField);
      }
    }
  }, [remainingInstallmentsField, prevInstallments, adjustedInstallments]);

  // Reset persistent recommendation and flags when editing is toggled off
  useEffect(() => {
    if (!editing) {
      setPersistentRecommendation(null);
      setLastAdjustedAmount(null);
      setPrevInstallments(remainingInstallmentsField);
    }
  }, [editing, remainingInstallmentsField]);

  // Show only persistent recommendation after auto-adjustment has happened
  // (Don't fall back to calculated one, or message won't hide when user edits)
  const displayRecommendationInfo = persistentRecommendation;

  const actionFooter = () => {
    return (
      <div className="w-full">
        <Button
          label="Save"
          icon="pi pi-check"
          onClick={submit}
          loading={saveButtonLoading}
          disabled={!submitButtonEnabled}
        />
        <Button
          label="Cancel"
          onClick={() => {
            resetForm();
            setEditing(false);
            setDifferenceByProvider({});
            setValuesByProvider({});
          }}
          icon="pi pi-times"
          style={{ marginLeft: "0.5em" }}
          className="p-button-outlined-light"
        />
      </div>
    );
  };
  const actionHeader = () => {
    return (
      <div className="flex gap-2 align-items-center">
        {editButtonDisabled && (
          <p className="text-primary-400">Transfer is being processed</p>
        )}
        <Button
          icon="pi pi-pencil"
          onClick={() => setEditing(true)}
          disabled={editButtonDisabled}
          className="p-button-outlined-light"
        />
      </div>
    );
  };

  const onNextInstallmentDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void
  ) => {
    const newValue = e.target.value;
    let currentValue = "";
    if (paymentPlan?.installments?.length) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset today's time to midnight
      const sortedInstallments = [...paymentPlan.installments]
        .filter((installment) => {
          const installmentDate = new Date(installment.dateDue);
          installmentDate.setHours(0, 0, 0, 0); // Reset installment's time to midnight
          return installmentDate >= today; // Compare only the date parts
        })
        .sort(
          (a, b) =>
            new Date(a.dateDue).getTime() - new Date(b.dateDue).getTime()
        ); // Sort by dateDue

      // first installment with a null paymentId
      const firstNullPaymentIdInstallment = sortedInstallments.find(
        (installment) => !installment.paymentId
      );
      currentValue = firstNullPaymentIdInstallment?.dateDue
        ? parseDate(firstNullPaymentIdInstallment.dateDue, false, true)
        : "-";
    }

    setValue("nextInstallmentDateChanged", newValue !== currentValue);
    handleChangeDateWithMask(e, onChange);
  };

  return (
    <StyledCard
      title="Payment details"
      header={
        refundedPayment && (
          <div className="flex justify-content-end pt-2 pr-3">
            <Chip variant={ChipVariants.WARNING}>Refunded</Chip>
          </div>
        )
      }
    >
      <div className="formgrid grid w-100">
        <div className="field flex flex-column col-12 md:col-4">
          <Label htmlFor="paymentDate">Payment Date</Label>

          <InputText
            disabled
            id="paymentDate"
            aria-labelledby="paymentDate"
            value={
              paidPayment?.createdAt
                ? parseDate(paidPayment.createdAt, false, true)
                : "-"
            }
          />
        </div>

        <div className="field flex flex-column col-12 md:col-4">
          <Label htmlFor="paymentAmount">Payment Amount</Label>

          <InputText
            disabled
            id="paymentAmount"
            aria-labelledby="paymentAmount"
            value={
              paidPayment?.amountInCents
                ? parsePrice(centsToDollars(paidPayment.amountInCents))
                : "-"
            }
          />
        </div>

        <div className="field flex flex-column col-12 md:col-4">
          <Label htmlFor="depositDate">Deposit Date</Label>

          <InputText
            disabled
            id="depositDate"
            aria-labelledby="depositDate"
            value={depositDate}
          />
        </div>

        <div className="field flex flex-column col-12 md:col-4">
          <Label htmlFor="paymentMethod">Payment Method</Label>

          <InputText
            disabled
            id="paymentMethod"
            value={paidPayment?.type || "-"}
            aria-labelledby="paymentMethod"
          />
        </div>

        <div className="field flex flex-column col-12 md:col-4">
          <Label htmlFor="paymentStatus">Status</Label>

          <InputText
            disabled
            id="paymentStatus"
            value={paidPayment?.status || "-"}
            aria-labelledby="paymentStatus"
          />
        </div>
      </div>
      {showPaymentPlan && (
        <div className="flex flex-column gap-2">
          <div className="flex justify-content-between w-full">
            <p className="font-bold text-lg mb-3">Payment Plan</p>
            <div style={{ height: "46px" }}>
              {!editing &&
                editable &&
                orderStatus !== orderStatusConstants.NO_BALANCE_DUE &&
                actionHeader()}
            </div>
          </div>
          <div className="formgrid grid w-100">
            <div className="field flex flex-column col-12 md:col-3">
              <Label
                htmlFor="paymentPlanMinDownPaymentType"
                data-testid="paymentPlanMinDownPaymentType"
              >
                Minimum down payment type
              </Label>
              <Controller
                name="paymentPlanMinDownPaymentType"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    id="paymentPlanMinDownPaymentType"
                    data-testid="minDownPaymentType_PaymentPlan"
                    value={value}
                    disabled={!minDownPaymentEnabled}
                    onChange={onChange}
                    options={["Percentage", "Flat amount"]}
                    placeholder="Select minimum down payment type"
                    className="w-full"
                  />
                )}
              />
            </div>
            {showPercentageField && (
              <div className="field flex flex-column col-12 md:col-4">
                <Label htmlFor="paymentPlanMinDownPaymentPercent">
                  Minimum Down Payment
                </Label>
                <Controller
                  data-testid="paymentPlanMinDownPaymentPercent"
                  name="paymentPlanMinDownPaymentPercent"
                  control={control}
                  rules={{
                    required:
                      validationConstants.PAYMENT_PLAN.MIN_DOWN_PAYMENT_PERCENT,
                  }}
                  render={({ field: { value, onChange, onBlur } }) => (
                    <>
                      <InputNumber
                        disabled={!minDownPaymentEnabled}
                        value={value}
                        min={5}
                        onValueChange={onChange}
                        onBlur={() =>
                          handleOnBlurField({
                            onBlur,
                            field: "paymentPlanMinDownPaymentPercent",
                            trigger,
                          })
                        }
                        suffix="%"
                        inputId="paymentPlanMinDownPaymentPercent"
                        data-testid="paymentPlanMinDownPaymentPercent"
                      />
                      {getFormErrorMessage(
                        "paymentPlanMinDownPaymentPercent",
                        errors
                      )}
                    </>
                  )}
                />
              </div>
            )}
            {showFlatAmountField && (
              <div className="field flex flex-column col-12 md:col-3">
                <Label
                  htmlFor="paymentPlanMinDownPaymentFlat"
                  data-testid="paymentPlanMinDownPaymentFlat"
                >
                  Minimum first payment flat
                </Label>

                <Controller
                  name="paymentPlanMinDownPaymentFlat"
                  control={control}
                  rules={{
                    required:
                      validationConstants.PAYMENT_PLAN.MIN_DOWN_PAYMENT_FLAT,
                    validate: validateMinDownPaymentFlatWithState,
                  }}
                  render={({ field: { onChange, value, onBlur } }) => (
                    <>
                      <InputNumber
                        value={minDownPaymentFlatDisplayValue}
                        currency="USD"
                        locale="en-US"
                        mode="currency"
                        disabled={!minDownPaymentEnabled}
                        onBlur={() =>
                          handleOnBlurField({
                            onBlur,
                            field: "paymentPlanMinDownPaymentFlat",
                            trigger,
                          })
                        }
                        inputId="paymentPlanMinDownPaymentFlat"
                        data-testid="minDownPaymentFlat_PaymentPlan"
                        onValueChange={(e) => {
                          // When user changes value, calculate total with fees for storage
                          const newValue = e.value;
                          const valueToStore =
                            newValue &&
                            order?.clearPatientFeeValueInPercentage &&
                            orderType === orderTypeConstants.GFE
                              ? newValue *
                                (1 +
                                  order.clearPatientFeeValueInPercentage / 100)
                              : newValue;
                          onChange(valueToStore);
                        }}
                        min={10}
                        aria-autocomplete="none"
                      />
                      {value &&
                        order?.clearPatientFeeValueInPercentage &&
                        orderType === orderTypeConstants.GFE && (
                          <div className="mt-2">
                            <p className="text-sm mb-1 text-primary">
                              with fees: {parsePrice(value)}
                            </p>
                          </div>
                        )}
                      {getFormErrorMessage(
                        "paymentPlanMinDownPaymentFlat",
                        errors
                      )}
                    </>
                  )}
                />
              </div>
            )}
            {editable && (
              <div className="field flex flex-column col-12 md:col-4">
                <Label htmlFor="paymentPlanDuration">
                  Remaining Installments
                </Label>
                <Controller
                  data-testid="paymentPlanDuration"
                  name="paymentPlanDuration"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <InputNumber
                      disabled={!editing}
                      id="paymentPlanDuration"
                      aria-labelledby="paymentPlanDuration"
                      value={value}
                      onValueChange={onChange}
                      min={0}
                      max={50}
                    />
                  )}
                />
              </div>
            )}
            {showRemainingAmountField && (
              <div className="field flex flex-column col-12 md:col-4">
                <Label htmlFor="paymentPlanRemainingAmount">
                  Remaining Amount
                </Label>
                <Controller
                  data-testid="paymentPlanRemainingAmount"
                  name="paymentPlanRemainingAmount"
                  control={control}
                  render={({ field: { value, onChange } }) =>
                    remainingAmountDisabled && !value ? (
                      <InputText value="-" disabled />
                    ) : (
                      <InputNumber
                        disabled={remainingAmountDisabled}
                        value={value}
                        currency="USD"
                        locale="en-US"
                        mode="currency"
                        inputId="paymentPlanRemainingAmount"
                        data-testid="paymentPlanRemainingAmount"
                        onValueChange={onChange}
                        min={0}
                      />
                    )
                  }
                />
              </div>
            )}
            {showNextInstallmentDate && (
              <div className="field flex flex-column col-12 md:col-4">
                <Label htmlFor="nextInstallmentDate">
                  Next Installment Date
                </Label>
                <Controller
                  data-testid="nextInstallmentDate"
                  name="nextInstallmentDate"
                  control={control}
                  rules={{
                    validate: (date) =>
                      validateNextInstallmentDate(date, "future"),
                    required:
                      validationConstants.PAYMENT_PLAN.NEXT_INSTALLMENT_DATE,
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <>
                      <InputText
                        disabled={!editing}
                        id="nextInstallmentDate"
                        value={value || ""}
                        placeholder="99/99/9999"
                        onBlur={() =>
                          handleOnBlurField({
                            onBlur,
                            field: "nextInstallmentDate",
                            trigger,
                          })
                        }
                        onChange={(e) =>
                          onNextInstallmentDateChange(e, onChange)
                        }
                      />

                      {getFormErrorMessage("nextInstallmentDate", errors)}
                    </>
                  )}
                />
              </div>
            )}
            {orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY && (
              <div className="field flex-column col-12 md:col-3">
                <Label
                  htmlFor="providerDistributionLogic"
                  data-testid="providerDistributionLogic"
                >
                  Provider Distribution Logic
                </Label>
                <Controller
                  name="providerDistributionLogic"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      id="providerDistributionLogic"
                      data-testid="providerDistributionLogic_PaymentPlan"
                      value={value}
                      onChange={onChange}
                      disabled={!editing}
                      options={Object.values(distributionLogicOptions)}
                      placeholder="Select provider distribution logic"
                      className="w-full"
                    />
                  )}
                />
              </div>
            )}
            {showPaymentPlanEnableButton && (
              <div className="field flex flex-column col-12 md:col-3">
                <Label
                  htmlFor="paymentPlanEnabled"
                  data-testid="paymentPlanEnabled"
                >
                  Payment Plan Enabled
                </Label>
                <Controller
                  name="paymentPlanEnabled"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <ToggleButton
                      checked={value}
                      onChange={onChange}
                      className="w-8rem"
                      disabled={!editing}
                    />
                  )}
                />
              </div>
            )}
            {showRemainingAmountsSection && (
              <RemainingAmountEditTable
                key={`${order?.updatedAt}-${order?.id}`}
                remainingAmountBreakdown={remainingAmountBreakdown}
                differenceByProvider={differenceByProvider}
                setDifferenceByProvider={setDifferenceByProvider}
                setValuesByProvider={setValuesByProvider}
                valuesByProvider={valuesByProvider}
                clearFeePercentage={Number(
                  paymentPlanBreakDown?.clearFee?.valueInPercentage
                )}
                clearPatientFeePercentage={
                  order?.clearPatientFeeValueInPercentage
                }
                isGFE={orderType === orderTypeConstants.GFE}
                initialPaymentPlanRemainingAmount={
                  paymentPlan?.remainingAmountInCents || 0
                }
                remainingInstallments={remainingInstallmentsField}
                calculatePaymentInfo={calculatePaymentInfo}
                accurateRemainingTotal={accurateRemainingTotal}
              />
            )}
          </div>
          <div>
            {monthlyPaymentInfo && (
              <>
                <div className="flex w-full bg-gray-100 border-t-1 border-gray-300 py-3 gap-4">
                  <div className="w-9 flex align-items-center justify-content-end">
                    <p className="font-bold">Remaining amount:</p>
                  </div>
                  <div className="w-3">
                    <p className="font-bold">
                      {parsePrice(remainingAmountField || 0)}{" "}
                      {isPaymentPlanRemainingAmountChanged &&
                        `(previously ${parsePrice(
                          centsToDollars(
                            paymentPlan?.remainingAmountInCents || 0
                          )
                        )})`}
                    </p>
                  </div>
                </div>
                <div className="flex w-full bg-gray-100 border-y-1 border-gray-300 py-3 gap-4">
                  <div className="w-9 flex align-items-center justify-content-end">
                    <p className="font-bold">Monthly Payment Amount:</p>
                  </div>
                  <div className="w-3">
                    <p className="font-bold">{monthlyPaymentInfo}</p>
                  </div>
                </div>
              </>
            )}
            {displayRecommendationInfo && (
              <div className="flex w-full bg-gray-100 border-bottom-1 border-gray-300 py-3 gap-4">
                <div className="w-9 flex align-items-center justify-content-end gap-2">
                  <i className="pi pi-exclamation-triangle text-orange-500 text-base" />
                  <p className="text-base font-medium text-orange-500">
                    Attention:
                  </p>
                </div>
                <div className="w-3">
                  <p className="text-sm font-medium text-orange-700">
                    {displayRecommendationInfo}
                  </p>
                </div>
              </div>
            )}
          </div>
          {editing && actionFooter()}
        </div>
      )}
    </StyledCard>
  );
}

export default PaymentDetails;
